import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';

import { useFeatureFlags } from '../../contexts/FeatureFlagContext';

const HeroSection: React.FC = () => {
  const { isEnabled } = useFeatureFlags();
  const regOpen = isEnabled('registration_open');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 50%, ${theme.palette.secondary.main} 100%)`,
                display: 'flex',
                alignItems: 'center',
                py: { xs: 8, md: 0 },
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Background decorative elements */}
            <Box
                sx={{
                    position: 'absolute',
                    width: '600px',
                    height: '600px',
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.05)',
                    top: '-200px',
                    right: '-200px',
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    width: '400px',
                    height: '400px',
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.03)',
                    bottom: '-100px',
                    left: '-100px',
                }}
            />

            <Container maxWidth="lg">
                <Grid container spacing={6} alignItems="center">
                    {/* Left Content */}
                    <Grid item xs={12} md={6}>
                        <Typography
                            variant="h1"
                            sx={{
                                fontSize: { xs: '2.5rem', md: '3.5rem' },
                                fontWeight: 'bold',
                                color: 'white',
                                mb: 2,
                                textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
                                lineHeight: 1.2,
                            }}
                        >
                            Your Trips. Your Story. Your Circle.
                        </Typography>

                        <Typography
                            variant="h5"
                            sx={{
                                fontSize: { xs: '1.1rem', md: '1.3rem' },
                                color: 'rgba(255, 255, 255, 0.9)',
                                mb: 4,
                                lineHeight: 1.6,
                            }}
                        >
                            Document your journeys, share with the people you choose, and discover trips from travelers around the world. Privacy-first travel sharing — you control who sees what.
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mb: 4 }}>
                            <Button
                                variant="outlined"
                                size={isMobile ? 'medium' : 'large'}
                                onClick={() => navigate('/demo')}
                                sx={{
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: '30px',
                                    fontWeight: 'bold',
                                    textTransform: 'none',
                                    fontSize: '1.1rem',
                                    borderColor: 'rgba(255, 255, 255, 0.5)',
                                    color: 'white',
                                    '&:hover': {
                                        borderColor: 'white',
                                        background: 'rgba(255, 255, 255, 0.1)',
                                    },
                                }}
                            >
                                See How It Works
                            </Button>
                            <Button
                                variant="contained"
                                color="secondary"
                                size={isMobile ? 'medium' : 'large'}
                                onClick={() => navigate(regOpen ? '/signup' : '/early-access')}
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: '30px',
                                    fontWeight: 'bold',
                                    textTransform: 'none',
                                    fontSize: '1.1rem',
                                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)',
                                }}
                            >
                                Get Started Free
                            </Button>
                        </Box>

                        {/* Stats - Real global travel statistics */}
                        <Box sx={{ display: 'flex', gap: 4 }}>
                            <Box>
                                <Typography sx={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white' }}>
                                    200+
                                </Typography>
                                <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem' }}>
                                    Photos Per Trip
                                </Typography>
                            </Box>
                            <Box>
                            <Typography sx={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white' }}>
                            8+
                            </Typography>
                            <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem' }}>
                            Bookings Per Trip
                            </Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white' }}>
                                    4x
                                </Typography>
                                <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem' }}>
                                    More Trusted From Friends
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Right Content - CLICKABLE Card */}
                    <Grid item xs={12} md={6}>
                        <Card
                            onClick={() => navigate('/explore')}
                            sx={{
                                borderRadius: '24px',
                                overflow: 'hidden',
                                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-8px)',
                                    boxShadow: '0 30px 80px rgba(0, 0, 0, 0.4)',
                                },
                            }}
                        >
                            {/* VIBRANT GRADIENT with COLORFUL GLOBE EMOJI */}
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '350px',
                                    background: `linear-gradient(145deg,
                                        #00D4AA 0%,
                                        ${theme.palette.primary.main} 50%,
                                        ${theme.palette.secondary.main} 100%)`,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative',
                                    overflow: 'hidden',
                                }}
                            >
                                {/* Decorative circles */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        width: '250px',
                                        height: '250px',
                                        borderRadius: '50%',
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        top: '-80px',
                                        right: '-80px',
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        width: '150px',
                                        height: '150px',
                                        borderRadius: '50%',
                                        background: 'rgba(255, 255, 255, 0.08)',
                                        bottom: '-40px',
                                        left: '-40px',
                                    }}
                                />

                                {/* COLORFUL WORLD GLOBE EMOJI */}
                                <Typography
                                    sx={{
                                        fontSize: '8rem',
                                        lineHeight: 1,
                                        mb: 2,
                                        filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3))',
                                    }}
                                >
                                    🌍
                                </Typography>

                                <Typography
                                    variant="h4"
                                    sx={{
                                        color: 'white',
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                                        fontSize: { xs: '1.6rem', md: '2rem' },
                                    }}
                                >
                                    Explore the World
                                </Typography>
                            </Box>

                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Join Our Community
                                </Typography>
                                <Typography sx={{ color: 'text.secondary', mb: 2.5, lineHeight: 1.6 }}>
                                    Document trips, share photos, and connect with your travel circle — all in one place.  
                                </Typography>

                                {/* Icons matching How It Works style */}
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Box
                                        onClick={(e) => { e.stopPropagation(); navigate('/community'); }}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            px: 2,
                                            py: 1,
                                            background: 'transparent',
                                            border: `2px solid ${theme.palette.primary.main}`,
                                            borderRadius: '12px',
                                            color: theme.palette.primary.main,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            '&:hover': { bgcolor: `${theme.palette.primary.main}10` },
                                        }}
                                    >
                                        <RocketLaunchOutlinedIcon sx={{ fontSize: '1.2rem' }} />
                                        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>Explore</Typography>      
                                    </Box>
                                    <Box
                                        onClick={(e) => { e.stopPropagation(); navigate('/login?redirect=%2Fcommunity%3Ftab%3Dfriends'); }}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            px: 2,
                                            py: 1,
                                            background: 'transparent',
                                            border: `2px solid ${theme.palette.secondary.main}`,
                                            borderRadius: '12px',
                                            color: theme.palette.secondary.main,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            '&:hover': { bgcolor: `${theme.palette.secondary.main}10` },
                                        }}
                                    >
                                        <GroupsOutlinedIcon sx={{ fontSize: '1.2rem' }} />
                                        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>Connect</Typography>      
                                    </Box>
                                    <Box
                                        onClick={(e) => { e.stopPropagation(); navigate('/login?redirect=%2Ftrips%2Fnew'); }}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            px: 2,
                                            py: 1,
                                            background: 'transparent',
                                            border: `2px solid ${theme.palette.primary.main}`,
                                            borderRadius: '12px',
                                            color: theme.palette.primary.main,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            '&:hover': { bgcolor: `${theme.palette.primary.main}10` },
                                        }}
                                    >
                                        <MapOutlinedIcon sx={{ fontSize: '1.2rem' }} />
                                        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>Plan</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default HeroSection;
